import React, {useEffect,useState,useRef} from 'react';
import {FormValidator, useFormValidator, Inputs, TextArea, useTariffableInputs} from 'coaz-ui';
import client from '../client';
import { LiaPlusSolid } from 'react-icons/lia';
import { PiTrashLight } from 'react-icons/pi';

const EditProgram = ({params, submitRef, setLoading, setEnableSubmit, initialProgram}) => {
    const [program,setProgram] = useState(null);
    const [addingProfessionalCategory,setAddingProfessionalCategory] = useState(false);
    const [selectedProfessionalCategory,setSelectedProfessionalCategory] = useState(null);
    const [addProfessionalCategory,setAddProfessionalCategory] = useState(null);
    const minWidth = 240;
    const {request,errorMessage} = client;
    const addButtonRef = useRef(null);

    const submit = async (e) => {
        const programId = program?.ref?.id ?? program?.id;
        /* console.info('[EditProgram] submitting update', {
            endpoint: 'programs',
            id: programId,
            fields: {
                name: program?.name,
                tariffApplicable: program?.tariffApplicable,
                criteriaPathItemId: program?.criteriaPathItemId,
                professionalCategories: program?.professionalCategories,
                description: program?.description,
            },
        }); */

        try {
            const response = await request("PUT", "programs", program);
            /* console.info('[EditProgram] update response', {
                id: response?.ref?.id,
                name: response?.name,
                hasReference: !!response?.ref,
            }); */

            if (response && response.ref) {
                const savedProgram = await request('GET', `programs/${programId}`);
                const fieldsToCheck = [
                    'name',
                    'tariffApplicable',
                    'criteriaPathItemId',
                    'professionalCategories',
                    'description',
                ];
                const getValue = (record, field) => {
                    if (field === 'criteriaPathItemId') {
                        return record?.criteriaPathItemId ??
                            record?.criteriaPathItem?.id ??
                            record?.criteriaPathItem?.ref?.id ??
                            null;
                    }
                    return record?.[field] ?? null;
                };
                const normalize = (field, value) => {
                    if (field === 'professionalCategories') {
                        const categories = Array.isArray(value) ? value : [];
                        return categories
                            .map((category) =>
                                String(
                                    typeof category === 'string'
                                        ? category
                                        : category?.name ?? category?.value ?? category?.id ?? ''
                                )
                            )
                            .sort();
                    }
                    return value == null ? null : String(value);
                };
                const mismatches = fieldsToCheck
                    .map((field) => ({
                        field,
                        submitted: getValue(program, field),
                        saved: getValue(savedProgram, field),
                    }))
                    .filter(
                        ({ field, submitted, saved }) =>
                            JSON.stringify(normalize(field, saved)) !==
                            JSON.stringify(normalize(field, submitted))
                    );

                /* console.info('[EditProgram] persisted update check', {
                    id: programId,
                    differences: mismatches,
                }); */

                if (mismatches.length > 0) {
                    return {
                        message: `The server accepted the update but did not save these fields: ${mismatches.map(({ field }) => field).join(', ')}`,
                    };
                }

                return {
                    message: 'Successful',
                    success: true
                };
            }

            return {
                message: errorMessage(response)
            };
        } catch (error) {
            console.error('[EditProgram] update failed', {
                status: error?.response?.status,
                message: error?.message,
            });
            return {
                message: errorMessage(error)
            };
        }
    };

    const [register,handleChange,handleSubmit,errors] = useFormValidator(submit);

    const {inputs: tariffInputs} = useTariffableInputs(program,setProgram,request,handleChange,register,errors);
    const inputs = tariffInputs.map((input) => ({
        ...input,
        register: undefined,
        checks: undefined,
        errors: undefined,
    }));

    const onChange = (e) => {
        const value = e.target.value;
        if(value === '') {
          setProgram({...program, [e.target.name]: null});
        } else {
          setProgram({...program, [e.target.name]: value});
        }
    }

    const add = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setAddingProfessionalCategory(true);
        if(addButtonRef.current) {
            let maxHeight = 200;
            let padding = 0;
            let itemHeight = 32;
            let avaiProfessionalCategories = [];
            await request('GET','professional-categories')
            .then((response) => {
                if(response && response.length > 0) {
                    for(let professionalCategory of response) {
                        if(!program.professionalCategories.find((programProfessionalCategory)=>{return programProfessionalCategory == professionalCategory})) {
                            avaiProfessionalCategories.push(professionalCategory);
                        }
                    }
                }
            })
            .catch((error) => {
                console.log(error.message);
            });
            let height = (padding * 2) + (avaiProfessionalCategories.length * itemHeight);
            height = height > maxHeight? maxHeight:height;
            let rect = addButtonRef.current.getBoundingClientRect();
            let vh = document.documentElement.clientHeight;
            let top = rect.bottom;
            if (rect.bottom + height > vh) {
                top = rect.top - height;
            }
            setAddProfessionalCategory({Component:() => 
                <div onClick={e => e.stopPropagation()} 
                    style={{left:rect.left+'px',top:top+'px',height:height+'px',paddingTop:padding+'px',paddingBottom:padding+'px'}} 
                    className='fixed flex flex-col w-fit shadow-lg bg-[rgb(238,238,238)] overflow-y-auto'>
                    {avaiProfessionalCategories.map((professionalCategory,i) => 
                        <div key={i} 
                            onClick={(e) => {
                                let programProfessionalCategories = program.professionalCategories;
                                programProfessionalCategories = [...programProfessionalCategories, professionalCategory];
                                setProgram({...program,professionalCategories:programProfessionalCategories});
                                setAddProfessionalCategory(null);
                                setAddingProfessionalCategory(false);
                            }} 
                            style={{height:itemHeight+'px'}} 
                            className='flex w-full shrink-0 items-center font-jostBook text-sm text-[rgb(93,93,93)] hover:text-white hover:bg-[rgb(0,175,240)] cursor-pointer'>
                                <p className='px-8'>{professionalCategory}</p>
                        </div>
                    )}
                </div>
            });
        }
    }

    useEffect(() => {
        if (program) {
            submitRef.current = {
                submit: () => {
                    if (!program.name?.trim()) {
                        return { message: 'Name is required' };
                    }
                    return submit();
                },
            };
            setEnableSubmit(true);
        } else {
            submitRef.current = null;
            setEnableSubmit(false);
        }

        return () => {
            submitRef.current = null;
        };
    }, [program]);

    useEffect(() => {
        ( async () => {
            if(!params || !params.id) return;
            setLoading && setLoading(true);

            if (initialProgram && String(initialProgram.ref?.id ?? initialProgram.id) === String(params.id)) {
                setProgram({
                    ...initialProgram,
                    professionalCategories: Array.isArray(initialProgram.professionalCategories)
                        ? [...initialProgram.professionalCategories]
                        : [],
                });
                setLoading && setLoading(false);
                return;
            }

            let prog = null;
            await request('GET',`programs/${params.id}`)
            .then((response) => {
                if(response && response.ref) {
                    prog = response;
                }
            })
            .catch((error) => {
                console.log(error);
            })

            if(prog && !prog.professionalCategories) {
                prog.professionalCategories = [];
            }
            setProgram(prog);
            setLoading && setLoading(false);
        }
        )();
    },[]);

    return (
        <FormValidator>
            {program && <div onClick={(e) => {
                    setAddProfessionalCategory(null);
                    setAddingProfessionalCategory(false);
                }}
                className='flex flex-col w-full h-auto'>
                <div className='flex flex-col w-full h-auto shrink-0 space-y-4'>
                    <Inputs
                        inputs={[
                            ...inputs,
                            { type: 'blank', name: 'criteriaPathSpacer' },
                        ]}
                        minWidth={minWidth}
                        onSubmit={handleSubmit}
                    />
                    <div className='flex flex-col w-full h-auto space-y-2'>
                        <p className='text-gray-600 text-sm capitalize'>Professional Categories</p>
                        <div className='flex flex-col w-full h-auto p-4 space-y-4 border rounded-lg'>
                            {program && program.professionalCategories && program.professionalCategories.map((professionalCategory,i) => 
                            <div key={i} 
                                onClick={(e) => {
                                    setSelectedProfessionalCategory(professionalCategory); 
                                }}
                                className='flex flex-row space-x-4'>
                                <p className={`flex flex-row w-full h-auto text-sm tracking-wider whitespace-nowrap overflow-ellipsis capitalize font-jostBook ${selectedProfessionalCategory === professionalCategory?'text-[rgb(0,175,240)]':''} cursor-pointer`}>
                                    {professionalCategory}
                                </p>
                            </div>)
                            }
                        </div>
                        <div className='flex flex-row w-auto h-auto space-x-4'>
                            <button ref={addButtonRef} 
                                onClick={(e) => {
                                    add(e);
                                }} 
                                className={`flex w-6 h-6 items-center justify-center ${addingProfessionalCategory?'border border-[rgb(0,175,240)] text-[rgb(0,175,240)]':'bg-[rgb(0,175,240)] text-white hover:bg-[rgba(0,175,240,.7)]'}  rounded-sm`}>
                                <LiaPlusSolid size={16}/>
                            </button>
                            <button onClick={(e) => {
                                    e.preventDefault();
                                    let professionalCategories = program.professionalCategories.filter((programProfessionalCategory => programProfessionalCategory !== selectedProfessionalCategory));
                                    setProgram({...program,professionalCategories:professionalCategories});
                                    setSelectedProfessionalCategory(null);
                                }} 
                                disabled={!selectedProfessionalCategory}
                                className={`flex w-6 h-6 items-center justify-center ${selectedProfessionalCategory?'bg-[rgb(0,175,240)] hover:bg-[rgba(0,175,240,.7)]':'bg-[rgba(0,175,240,.7)]'} text-white rounded-sm`}>
                                <PiTrashLight size={16}/>
                            </button>
                        </div>
                    </div>
                    <TextArea
                        label='Description'
                        id='description'
                        name='description'
                        value={program.description?program.description:''}
                        placeholder='Enter description'
                        onChange={(e) => handleChange(e,onChange)}
                    /> 
                </div>
            </div>}
            {addProfessionalCategory && <addProfessionalCategory.Component/>}
        </FormValidator>
      )
}

export default EditProgram